import React from 'react'
import { Badge, SectionCard } from '@enamelai/design-system'
import { Icon as BillingIcon } from '@enamelai/design-system/icons/Billing'
import { Icon as WalletIcon } from '@enamelai/design-system/icons/Wallet'
import type { ProfilePaymentMethodsSummary } from '@assets/mockdata/PatientTray/mockAccountSummary'
import ProfileEmptyState from './ProfileEmptyState.component'
import {
  formatProfileBankCountLabel,
  formatProfileCardCountLabel,
} from './patientProfilePaymentMethods.util'

type PatientProfilePaymentMethodsSummaryProps = {
  summary: ProfilePaymentMethodsSummary
  forceEmpty?: boolean
}

function PaymentDetailRow({
  label,
  value,
}: {
  label: string
  value: string
}): React.ReactElement {
  return (
    <div className="patient-profile-payment-summary-chip-row">
      <span className="patient-profile-payment-summary-chip-label">
        {label}
      </span>
      <span className="patient-profile-payment-summary-chip-value">
        {value}
      </span>
    </div>
  )
}

function PatientProfilePaymentMethodsSummary({
  summary,
  forceEmpty = false,
}: PatientProfilePaymentMethodsSummaryProps): React.ReactElement {
  const cards = forceEmpty ? [] : summary.cards
  const banks = forceEmpty ? [] : summary.banks
  const isEmpty = cards.length === 0 && banks.length === 0

  return (
    <SectionCard title="Payment methods" className="patient-profile-section">
      {isEmpty ? (
        <ProfileEmptyState
          icon={WalletIcon}
          title="No payment methods"
          description="You don't have any payment methods on record."
        />
      ) : (
        <div className="patient-profile-payment-summary-grid">
          <section className="patient-profile-payment-summary-group">
            <div className="patient-profile-payment-summary-group-head">
              <h3 className="patient-profile-payment-summary-group-title">
                Debit/Credit card
              </h3>
              <Badge
                mode="tinted"
                color="blue"
                label={formatProfileCardCountLabel(cards.length)}
              />
            </div>
            {cards.length > 0 ? (
              <div className="patient-profile-payment-summary-chips">
                {cards.map((card) => (
                  <div
                    key={card.id}
                    className="patient-profile-payment-summary-chip"
                  >
                    <PaymentDetailRow
                      label="Account ending:"
                      value={card.lastFour}
                    />
                    <PaymentDetailRow label="Card:" value={card.brand} />
                  </div>
                ))}
              </div>
            ) : (
              <ProfileEmptyState
                className="profile-empty-state--compact"
                icon={BillingIcon}
                title="No saved cards"
                description="You don't have any saved cards on record."
              />
            )}
          </section>

          <section className="patient-profile-payment-summary-group">
            <div className="patient-profile-payment-summary-group-head">
              <h3 className="patient-profile-payment-summary-group-title">
                Digital bank
              </h3>
              <Badge
                mode="tinted"
                color="blue"
                label={formatProfileBankCountLabel(banks.length)}
              />
            </div>
            {banks.length > 0 ? (
              <div className="patient-profile-payment-summary-chips">
                {banks.map((bank) => (
                  <div
                    key={bank.id}
                    className="patient-profile-payment-summary-chip"
                  >
                    <PaymentDetailRow
                      label="Account ending:"
                      value={bank.lastFour}
                    />
                    <PaymentDetailRow label="Bank" value={bank.bankName} />
                  </div>
                ))}
              </div>
            ) : (
              <ProfileEmptyState
                className="profile-empty-state--compact"
                icon={WalletIcon}
                title="No bank details"
                description="You don't have any bank details on record."
              />
            )}
          </section>
        </div>
      )}
    </SectionCard>
  )
}

export default PatientProfilePaymentMethodsSummary
